import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { isEmpty } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';

import type { SearchableSelectOption } from '@/components/SearchableSelect';
import { DEFAULT_PAGE_SIZE } from '@/constants/common';
import { useFetchDevicesByOrg } from '@/features/DeviceCreate/DeviceCreate.hooks';
import type { SearchFilters } from '@/features/SearchBar';
import useHandleError from '@/hooks/useHandleError';
import type {
  RequestCreateDTO,
  RequestDTO,
  RequestDTOKeys
} from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';

export const requestsTablePageAtom = atom<number>(0);
export const requestsTableRowsPerPageAtom = atom<number>(DEFAULT_PAGE_SIZE);
export const requestsTableOrderByAtom = atom<string>('id');
export const requestsAtom = atom<RequestDTO[]>([]);
export const requestDetailsExpandedAtom = atom<boolean>(false);
export const currentRequestAtom = atom<RequestDTO | null>(null);

export const requestsFiltersAtom = atom<SearchFilters>({});
export const sensorsListAtom = atom<SearchableSelectOption[]>([]);

export const useFetchRequests = () => {
  const [, setRequests] = useAtom(requestsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const { data, refetch, isFetching, isSuccess, isError, error } = useQuery({
    queryKey: [
      `organizations/${selectedOrganization.label}/requests?request_type=pcap`
    ],
    staleTime: 1000,
    enabled: true
  });
  const handleError = useHandleError();

  useEffect(() => {
    if (isSuccess) {
      setRequests(data as RequestDTO[]);
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (isError) {
      handleError(error as AxiosError, 'Failure to fetch requests.');
    }
  }, [isError]);

  return { data, refetch, isSuccess, isFetching };
};

export const filteredRequestsAtom = atom((get) => {
  const requests = get(requestsAtom);
  const filters = get(requestsFiltersAtom);

  if (isEmpty(filters)) return requests;

  return requests.filter((request) =>
    Object.keys(filters).every((key) =>
      filters[key].some((filter) => {
        if (key === 'any') {
          return JSON.stringify(request)
            .toLocaleLowerCase()
            .includes(filter.toLocaleLowerCase());
        }

        if (key === 'sensors') {
          return request.details.sensors.some((sensor) =>
            sensor.includes(filter)
          );
        }
        if (key === 'requester.name') {
          return request.requester.name
            .toLocaleLowerCase()
            .includes(filter.toLocaleLowerCase());
        }

        return request[key as RequestDTOKeys] === filter;
      })
    )
  );
});

export const useCreateRequest = () => {
  const { refetch } = useFetchRequests();
  return useMutation({
    mutationFn: (newRequest: RequestCreateDTO) => {
      return axiosClient.post(`/requests/`, newRequest);
    },
    onSuccess: () => {
      refetch();
      enqueueSnackbar('Request Created', {
        variant: 'success'
      });
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(`Failed to create request: ${error.message}`, {
        variant: 'error'
      });
    }
  });
};

export const useDeleteRequest = () => {
  const { refetch } = useFetchRequests();
  const [, setRequestDetailsExpanded] = useAtom(requestDetailsExpandedAtom);

  return useMutation({
    mutationFn: (requestId: number) => {
      return axiosClient.delete(`/requests/${requestId}`);
    },
    onSuccess: () => {
      refetch();
      enqueueSnackbar('Request Deleted', {
        variant: 'success'
      });
      setRequestDetailsExpanded(false);
    },
    onError: (error: AxiosError) => {
      enqueueSnackbar(`Failed to delete request: ${error.message}`, {
        variant: 'error'
      });
    }
  });
};

export const useFetchSensorsList = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const { isSuccess, data, isFetching } =
    useFetchDevicesByOrg(selectedOrganization);
  const [, setSensors] = useAtom(sensorsListAtom);

  useEffect(() => {
    if (isSuccess && data.other_devices.length > 0) {
      const sensorsList = data.other_devices
        .filter(
          (device) => device.devices_deployed?.device_type?.type === 'Sensor'
        )
        .map((device) => ({
          label: device.devices_deployed?.host_name || '',
          value: device.devices_deployed?.host_name || ''
        }));
      setSensors(sensorsList);
      return;
    }
    setSensors([]);
  }, [isSuccess, data]);

  return { isSuccess, data, isFetching };
};
